import type { LevelAssessmentModel } from "@shared/labs/level-assessment-model";
import { useEffect, useState } from "react";
import { Link } from "wouter";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { rpcClient } from "@/lib/rpc";
import { useActiveModel } from "./active-model-context";

/**
 * Dropdown that switches the active evaluation model.
 *
 * Reads the list of models on mount, displays them in a Select, defaults
 * to the active model from context, and on change updates the context
 * (which persists to localStorage and refetches downstream data).
 *
 * Compact layout: meant to sit at the top of the form view as a single
 * row. The label is read-only metadata ("Modelo de avaliação") so the
 * operator sees what they're picking; the trigger shows the active
 * model's display name; the dropdown lists every model with its axis
 * count + level range as secondary text.
 */
export function ModelPicker() {
  const { modelId, setModelId, loading: contextLoading } = useActiveModel();
  const [models, setModels] = useState<LevelAssessmentModel[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = (await rpcClient.labs.levelAssessmentModelsList({})) as {
          models: LevelAssessmentModel[];
        };
        if (cancelled) return;
        setModels(res.models);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : "Erro inesperado");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Compact label + value row.
  return (
    <div
      data-testid="model-picker"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        marginBottom: 16,
        flexWrap: "wrap",
      }}
    >
      <span
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: "var(--r-ink-muted)",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        Modelo de avaliação
      </span>
      <Select
        value={modelId}
        onValueChange={(v) => {
          if (v && v !== modelId) setModelId(v);
        }}
        disabled={contextLoading || models === null}
      >
        <SelectTrigger
          data-testid="model-picker-trigger"
          style={{ height: 34, minWidth: 280, fontSize: 13 }}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {models === null ? (
            <SelectItem value={modelId} disabled>
              Carregando modelos…
            </SelectItem>
          ) : models.length === 0 ? (
            <SelectItem value={modelId} disabled>
              Nenhum modelo disponível
            </SelectItem>
          ) : (
            models.map((m) => (
              <SelectItem key={m.id} value={m.id} data-testid={`model-picker-option-${m.id}`}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontWeight: 500 }}>{m.name}</span>
                  <span
                    style={{
                      fontSize: 11,
                      color: "var(--r-ink-muted)",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {m.crossAxes.length} competências
                    {m.disciplines.length > 0 ? ` · ${m.disciplines.length} disciplinas` : ""} · L
                    {m.levels.min}–L{m.levels.max}
                  </span>
                  {m.isSeed && (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        color: "var(--r-ink-muted)",
                        background: "var(--r-sand, #f4f1ea)",
                        padding: "1px 6px",
                        borderRadius: 999,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                      }}
                    >
                      padrão
                    </span>
                  )}
                </span>
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>
      <Link
        href="/labs/level-assessment/evaluate"
        data-testid="model-picker-evaluate-link"
        style={{
          fontSize: 12,
          color: "var(--r-ink-muted)",
          textDecoration: "underline",
          textDecorationStyle: "dotted",
          textUnderlineOffset: 3,
        }}
      >
        Avaliação avulsa
      </Link>
      <Link
        href="/labs/level-assessment/models"
        data-testid="model-picker-manage-link"
        style={{
          fontSize: 12,
          color: "var(--r-ink-muted)",
          textDecoration: "underline",
          textDecorationStyle: "dotted",
          textUnderlineOffset: 3,
        }}
      >
        Gerenciar modelos
      </Link>
      <Link
        href="/labs/level-assessment/calibration-data"
        data-testid="model-picker-calibration-data-link"
        style={{
          fontSize: 12,
          color: "var(--r-ink-muted)",
          textDecoration: "underline",
          textDecorationStyle: "dotted",
          textUnderlineOffset: 3,
        }}
      >
        Dados de calibração
      </Link>
      <Link
        href="/labs/level-assessment/sets"
        data-testid="model-picker-sets-link"
        style={{
          fontSize: 12,
          color: "var(--r-ink-muted)",
          textDecoration: "underline",
          textDecorationStyle: "dotted",
          textUnderlineOffset: 3,
        }}
      >
        Pacotes de candidatos
      </Link>
      <Link
        href="/labs/level-assessment/candidates"
        data-testid="model-picker-candidates-link"
        style={{
          fontSize: 12,
          color: "var(--r-ink-muted)",
          textDecoration: "underline",
          textDecorationStyle: "dotted",
          textUnderlineOffset: 3,
        }}
      >
        Candidatos monitorados
      </Link>
      {error && (
        <span
          style={{
            fontSize: 12,
            color: "var(--r-coral, #f3354b)",
          }}
        >
          Não foi possível carregar os modelos.
        </span>
      )}
    </div>
  );
}
